import React from "react";
import SectionTitle from "../components/SectionTitle";
import ContactForm from "../components/ContactForm";
import { useLang } from "../context/LangContext";
import { Home as HomeIcon, ConciergeBell, Users, Shield, PlaneTakeoff } from "lucide-react";

const COPY = {
  fr: {
    eyebrow: "VILLA & CONCIERGERIE",
    title: "Le protocole à domicile, orchestré par votre conciergerie.",
    subtitle: "Riviera Shockwaves intervient directement dans votre villa, résidence privée, chalet ou lieu de séjour — Monaco, Côte d'Azur, et au-delà par hélicoptère jusqu'à 400 km à vol d'oiseau.",
    cards: [
      { icon: HomeIcon, k: "Villa privée", d: "Installation complète dans un espace choisi de votre résidence — 90 minutes de mise en place, sans logistique de votre côté." },
      { icon: ConciergeBell, k: "Conciergerie & family office", d: "Coordination confidentielle avec votre équipe. Planning, briefing, reporting — un interlocuteur unique." },
      { icon: Users, k: "Groupe & invités", d: "De 5 à 12 personnes prises en charge par journée. Sessions individuelles synchronisées." },
      { icon: Shield, k: "Discrétion absolue", d: "Praticiens tenus au secret professionnel. Aucune trace numérique de votre passage sans votre accord explicite." },
    ],
    reach_eyebrow: "ZONE D'INTERVENTION",
    reach_title: "Physique ou par hélicoptère.",
    reach_lines: [
      "Déplacement physique sur devis : Monaco · Cala del Forte · Cannes · Saint-Tropez · Nice · Antibes · Beaulieu · Cap-Ferrat · Menton.",
      "Au-delà de 70 km, sur devis selon destination — hélicoptère via nos partenaires aériens, jusqu'à 400 km à vol d'oiseau : Saint-Tropez, Portofino, Courchevel, Megève, Sardaigne nord, Corse.",
      "Toute autre destination sur devis, avec coordination logistique dédiée.",
    ],
    reach_note: "Les frais d'hélicoptère, de transfert et d'accueil sont précisés au devis. Un interlocuteur unique orchestre l'ensemble.",
  },
  en: {
    eyebrow: "VILLA & CONCIERGE",
    title: "The protocol at home, orchestrated by your concierge.",
    subtitle: "Riviera Shockwaves operates directly in your villa, private residence, chalet or stay location — Monaco, French Riviera, and beyond by helicopter up to 400 km as the crow flies.",
    cards: [
      { icon: HomeIcon, k: "Private villa", d: "Complete setup in a chosen space of your residence — 90-minute install, zero logistics on your side." },
      { icon: ConciergeBell, k: "Concierge & family office", d: "Confidential coordination with your team. Planning, briefing, reporting — a single point of contact." },
      { icon: Users, k: "Group & guests", d: "From 5 to 12 people per day. Synchronised individual sessions." },
      { icon: Shield, k: "Absolute discretion", d: "Practitioners bound by professional secrecy. No digital trace of your visit without your explicit consent." },
    ],
    reach_eyebrow: "COVERAGE AREA",
    reach_title: "By road or by helicopter.",
    reach_lines: [
      "Ground delivery, quoted per destination: Monaco · Cala del Forte · Cannes · Saint-Tropez · Nice · Antibes · Beaulieu · Cap-Ferrat · Menton.",
      "Beyond 70 km, on quote depending on destination — helicopter via our air partners, up to 400 km as the crow flies: Saint-Tropez, Portofino, Courchevel, Megève, northern Sardinia, Corsica.",
      "Any other destination on quote, with dedicated logistics coordination.",
    ],
    reach_note: "Helicopter, transfer and welcome fees are detailed on quote. A single point of contact orchestrates everything.",
  },
  it: {
    eyebrow: "VILLA & CONCIERGERIE",
    title: "Il protocollo a domicilio, orchestrato dalla vostra conciergerie.",
    subtitle: "Riviera Shockwaves interviene direttamente nella vostra villa, residenza privata, chalet o luogo di soggiorno — Monaco, Costa Azzurra, e oltre in elicottero fino a 400 km in linea d'aria.",
    cards: [
      { icon: HomeIcon, k: "Villa privata", d: "Installazione completa in uno spazio scelto della vostra residenza — 90 minuti di allestimento, nessuna logistica dalla vostra parte." },
      { icon: ConciergeBell, k: "Conciergerie & family office", d: "Coordinamento riservato con il vostro team. Pianificazione, briefing, reporting — un unico interlocutore." },
      { icon: Users, k: "Gruppo & ospiti", d: "Da 5 a 12 persone gestite al giorno. Sessioni individuali sincronizzate." },
      { icon: Shield, k: "Discrezione assoluta", d: "Professionisti vincolati dal segreto professionale. Nessuna traccia digitale del vostro passaggio senza esplicito consenso." },
    ],
    reach_eyebrow: "AREA DI INTERVENTO",
    reach_title: "Su strada o in elicottero.",
    reach_lines: [
      "Trasferta su strada, su preventivo: Monaco · Cala del Forte · Cannes · Saint-Tropez · Nizza · Antibes · Beaulieu · Cap-Ferrat · Mentone.",
      "Oltre i 70 km, su preventivo in base alla destinazione — elicottero tramite i nostri partner aerei, fino a 400 km in linea d'aria: Saint-Tropez, Portofino, Courchevel, Megève, Sardegna nord, Corsica.",
      "Ogni altra destinazione su preventivo, con coordinamento logistico dedicato.",
    ],
    reach_note: "Spese di elicottero, trasferimento e accoglienza sono dettagliate sul preventivo. Un unico interlocutore orchestra il tutto.",
  },
  ru: {
    eyebrow: "ВИЛЛА & КОНСЬЕРЖ",
    title: "Протокол на дому под управлением вашего консьержа.",
    subtitle: "Riviera Shockwaves работает непосредственно на вашей вилле, в частной резиденции, шале или месте пребывания — Монако, Лазурный берег, и далее на вертолёте до 400 км по прямой.",
    cards: [
      { icon: HomeIcon, k: "Частная вилла", d: "Полная установка в выбранном пространстве резиденции — 90 минут на монтаж, без логистики с вашей стороны." },
      { icon: ConciergeBell, k: "Консьерж & family office", d: "Конфиденциальная координация с вашей командой. Планирование, брифинг, отчёт — единая точка контакта." },
      { icon: Users, k: "Группа & гости", d: "От 5 до 12 человек в день. Синхронизированные индивидуальные сеансы." },
      { icon: Shield, k: "Абсолютная конфиденциальность", d: "Специалисты связаны профессиональной тайной. Никаких цифровых следов вашего визита без вашего явного согласия." },
    ],
    reach_eyebrow: "ЗОНА ВЫЕЗДА",
    reach_title: "По земле или на вертолёте.",
    reach_lines: [
      "Наземный выезд по расчёту: Монако · Кала-дель-Форте · Канны · Сен-Тропе · Ницца · Антиб · Больё · Кап-Ферра · Ментон.",
      "Свыше 70 км — по расчёту в зависимости от направления: вертолёт через наших авиапартнёров, до 400 км по прямой: Сен-Тропе, Портофино, Куршевель, Межев, север Сардинии, Корсика.",
      "Любое другое направление по запросу с индивидуальной логистикой.",
    ],
    reach_note: "Расходы на вертолёт, трансфер и приём указываются в смете. Единая точка контакта организует всё.",
  },
};

export default function VillaConciergerie() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  return (
    <main className="pt-32" data-testid="page-villa">
      <section className="container-luxe py-16">
        <SectionTitle eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} />
      </section>

      <section className="container-luxe pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {c.cards.map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="beam-card p-7 md:p-8" data-testid={`villa-card-${i}`}>
                <div className="flex items-center gap-3">
                  <Icon size={22} className="text-[var(--gold)]" />
                  <span className="overline">0{i + 1}</span>
                </div>
                <h3 className="font-display text-xl md:text-2xl text-[var(--ink)] mt-5 font-light leading-tight">{it.k}</h3>
                <p className="mt-3 text-[14px] text-[var(--text-secondary)] leading-relaxed font-body">{it.d}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Coverage — helicopter */}
      <section className="bg-[var(--surface-2)] hairline-t hairline-b py-20 md:py-24" data-testid="villa-reach">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-4">{c.reach_eyebrow}</div>
            <h2 className="font-display text-3xl sm:text-4xl text-[var(--ink)] font-light leading-tight">{c.reach_title}</h2>
            <div className="mt-8 flex items-center gap-3 text-[var(--gold)]">
              <PlaneTakeoff size={24} />
              <span className="font-display text-2xl">400 km</span>
              <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] font-display">à vol d'oiseau</span>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-5">
            {c.reach_lines.map((line, i) => (
              <p key={i} className="text-[15px] text-[var(--ink)]/85 leading-relaxed font-body flex items-start gap-3">
                <span className="text-[var(--gold)] mt-[10px] w-3 h-px bg-[var(--gold)] shrink-0" />
                <span>{line}</span>
              </p>
            ))}
            <p className="mt-6 text-[13px] text-[var(--text-secondary)] italic font-body">{c.reach_note}</p>
          </div>
        </div>
      </section>

      <section className="container-luxe py-24">
        <ContactForm source="villa" />
      </section>
    </main>
  );
}
